import { api } from './api'

const apiVersion = 'v2'

export const integrationsApi = api
    .enhanceEndpoints({
        addTagTypes: ['Zapier'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            startXeroAuthorization: builder.mutation({
                query: ({ redirectUri }) => ({
                    url: `/${apiVersion}/integrations/xero`,
                    method: 'POST',
                    body: { redirectUri },
                    responseHandler: (response) => response.text(),
                }),
            }),

            exchangeXeroCode: builder.mutation({
                query: ({ code, redirectUri }) => ({
                    url: `/${apiVersion}/integrations/xero/exchange-code`,
                    method: 'POST',
                    body: { code, redirectUri },
                }),
            }),

            getXeroProfile: builder.mutation({
                query: ({ accessToken }) => ({
                    url: `/${apiVersion}/integrations/xero/profile`,
                    method: 'POST',
                    body: { accessToken },
                }),
            }),

            connectGoogleDrive: builder.query({
                query: ({ code, organisationId }) => ({
                    url: `/${apiVersion}/integrations/googledrive/connect/${organisationId}`,
                    params: {
                        authorizationCode: code,
                        redirectUri: window.location.origin,
                    },
                    responseHandler: (response) => response.text(),
                }),
            }),

            getGoogleDriveToken: builder.query({
                query: ({ organisationId }) => ({
                    url: `/${apiVersion}/integrations/googledrive/token/${organisationId}`,
                }),
            }),

            sendToGoogleDrive: builder.mutation({
                query: ({ quoteId }) => ({
                    url: `/${apiVersion}/integrations/googledrive/export/${quoteId}`,
                    method: 'POST',
                }),
            }),

            disconnectGoogleDrive: builder.mutation({
                query: ({ organisationId }) => ({
                    url: `/${apiVersion}/integrations/googledrive/disconnect/${organisationId}`,
                    method: 'PUT',
                }),
                invalidatesTags: ['Organisation'],
            }),

            createStripeAccount: builder.mutation({
                query: ({ organisationId }) => ({
                    url: `/organisations/${organisationId}/create-stripe-account`,
                    method: 'POST',
                    responseHandler: (response) => response.text(),
                }),
                invalidatesTags: ['Organisation'],
            }),

            createCustomerPortalSession: builder.mutation({
                query: ({ organisationId, requestModel }) => ({
                    url: `/organisations/${organisationId}/create-customer-portal-session`,
                    method: 'POST',
                    body: requestModel,
                }),
            }),

            getCustomerClientSecret: builder.query({
                query: ({ organisationId }) => ({
                    url: `/${apiVersion}/integrations/stripe/${organisationId}/customer-client-secret`,
                }),
            }),

            getStripeOnboardingLink: builder.query({
                query: ({ organisationId, params }) => ({
                    url: `/organisations/${organisationId}/get-stripe-onboarding-link`,
                    params,
                    responseHandler: (response) => response.text(),
                }),
            }),

            createStripeCheckoutSession: builder.mutation({
                query: ({ cancelUrl, organisationId, quoteId, successUrl }) => ({
                    url: `/${apiVersion}/integrations/stripe/${organisationId}/checkout/${quoteId}`,
                    method: 'POST',
                    body: { successUrl, cancelUrl },
                    responseHandler: (response) => response.text(),
                }),
            }),

            setIntegrationFlags: builder.mutation({
                query: ({ organisationDto }) => {
                    const { $type, ...requestModel } = organisationDto
                    return {
                        url: `/organisations/${organisationDto.organisationId}/integration-flags`,
                        method: 'PUT',
                        body: requestModel,
                    }
                },
                invalidatesTags: ['Organisation'],
            }),

            getIntercomToken: builder.query({
                query: () => ({
                    url: `/intercom/token`,
                }),
            }),

            getChurnkeyUserHash: builder.query({
                query: () => ({
                    url: `/${apiVersion}/integrations/churnkey`,
                    responseHandler: (response) => response.text(),
                }),
            }),

            disconnectXero: builder.mutation({
                query: ({ organisationId }) => ({
                    url: `/${apiVersion}/integrations/xero/disconnect`,
                    method: 'POST',
                    body: { organisationId },
                }),
            }),

            /**
             * Records which organisation a Zapier connection is being made for, ahead of handing the
             * browser to Kinde. The choice cannot ride the OAuth protocol, so the authorize page
             * writes it here and Zapier's token step reads it back.
             *
             * 204 on success. 409 means the user already has a connection in progress, 400 that
             * their account has not been migrated to Kinde, and 403 that they are not a member of
             * the organisation (the endpoint currently sends 401 for that case, which the global
             * 401 interceptor in baseQueryWithReauth signs the user out on before this ever renders
             * a message — see the bug raised on the integrations service to fix this).
             */
            recordZapierPendingAuthorization: builder.mutation({
                query: ({ organisationId }) => ({
                    url: `/${apiVersion}/integrations/zapier/pending-authorization`,
                    method: 'POST',
                    body: { organisationId },
                }),
            }),

            /**
             * Fetches a server-signed Paragon user token (RS256, sub = caller's organisation, 60 min).
             * Takes no arguments — the backend derives the subject from the authenticated caller.
             */
            getParagonToken: builder.query({
                query: () => ({
                    url: `/${apiVersion}/integrations/paragon/token`,
                }),
            }),

            /**
             * Whether the organisation has an active Zapier connection and who connected it. The
             * response body is snake_case (connected, connection_id, status, connected_at_utc,
             * disabled_at_utc, disabled_reason, connected_by_user_id, connected_by_name,
             * connected_by_email) — unlike the rest of this API — because it
             * passes the integrations service's contract straight through.
             */
            getZapierConnection: builder.query({
                query: ({ organisationId }) => ({
                    url: `/${apiVersion}/integrations/zapier/connection`,
                    params: { organisationId },
                }),
                providesTags: ['Zapier'],
            }),

            /**
             * The organisation's Zapier subscriptions (one per event type a Zap is built against),
             * including who built each one. Same snake_case response shape as getZapierConnection.
             */
            getZapierSubscriptions: builder.query({
                query: ({ organisationId }) => ({
                    url: `/${apiVersion}/integrations/zapier/subscriptions`,
                    params: { organisationId },
                }),
            }),

            /**
             * Revokes the caller's organisation's Zapier connection, which stops every Zap built on
             * it. Takes no arguments: the backend derives the organisation from the authenticated
             * caller so a token cannot be pointed at another organisation. 204 on success, also when
             * nothing was connected. 403 when the caller is not an organisation owner or
             * administrator, 502 when the integrations service could not be reached.
             *
             * Only the connection read carries the Zapier tag. The subscriptions read skips itself
             * once the connection reports disconnected, and refetching it in parallel would race the
             * connection refetch and flash its error state for a revoked connection.
             */
            disconnectZapier: builder.mutation({
                query: () => ({
                    url: `/${apiVersion}/integrations/zapier/disconnect`,
                    method: 'POST',
                }),
                invalidatesTags: ['Zapier'],
            }),
        }),
    })

export const {
    useCreateCustomerPortalSessionMutation,
    useCreateStripeAccountMutation,
    useCreateStripeCheckoutSessionMutation,
    useDisconnectGoogleDriveMutation,
    useDisconnectXeroMutation,
    useDisconnectZapierMutation,
    useExchangeXeroCodeMutation,
    useGetCustomerClientSecretQuery,
    useGetIntercomTokenQuery,
    useGetParagonTokenQuery,
    useGetStripeOnboardingLinkQuery,
    useGetXeroProfileMutation,
    useGetZapierConnectionQuery,
    useGetZapierSubscriptionsQuery,
    useLazyConnectGoogleDriveQuery,
    useLazyGetChurnkeyUserHashQuery,
    useLazyGetGoogleDriveTokenQuery,
    useLazyGetIntercomTokenQuery,
    useLazyGetStripeOnboardingLinkQuery,
    useRecordZapierPendingAuthorizationMutation,
    useSendToGoogleDriveMutation,
    useSetIntegrationFlagsMutation,
    useStartXeroAuthorizationMutation,
} = integrationsApi
